import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Flex, List, ListItem, SimpleGrid, Text } from "@chakra-ui/react";
import { UserSubscriptionPlan } from "../../constants/GlobalTypesAndEnumsAndConstants";
import {
  formatStripePriceDetails,
  initializePrices,
  StripePriceDetails,
} from "../../backend/utils/stripe-price-details";
import {
  calculateAnnualDiscountPercentageFromRawPrices,
  getAnnualProratedMonthlyPriceAmountForPlan,
  getFullAnnualPriceForPlan,
  getMonthlyPriceAmountForPlan,
} from "../../components/upsell/planPricingHelpers";
import { getMarketingPageHeading, MarketingPageLanguage } from "../../marketing-content/marketingContentLoader";
import { MARKETING_LOCALES } from "../../marketing-content/marketingLocales";
import { landing } from "../landing-page/landingTokens";
import { useLandingTheme } from "../landing-page/LandingThemeContext";
import { buildAppUrl, trackLandingCallToActionClick } from "../landing-page/handoff/appHandoff";
import { useAppUrl, useIsHydrated } from "../landing-page/handoff/useAppUrl";
import { ThemedContainer, ThemedPanel } from "../landing-page/sections/themed/ThemedLayout";
import { IconCheckmark } from "../../icons";
import { MarketingPageMain } from "./MarketingSiteShell";
import { MarketingTemplatePageProps } from "./marketingTemplatePageProps";
import { getMarketingPricingCopy } from "./marketingPricingCopy";

type BillingPeriod = "monthly" | "annual";
type PaidSubscriptionPlan = UserSubscriptionPlan.SUPER_LEARNER | UserSubscriptionPlan.ELITE;
type SubscriptionPricingDetails = StripePriceDetails;

// No signed-in user on the marketing site, so the prices are the US ones (the app's paywall passes the
// user's country). The same fetch and formatting as the paywall, without its in-app cache.
const loadSubscriptionPricingDetails = () => initializePrices("US").then(formatStripePriceDetails);

// The `subscription_plan_type` values the app's /upgrade page understands. The plan sold as "Pro" is
// `UserSubscriptionPlan.SUPER_LEARNER`.
const UPGRADE_PAGE_PLAN_TYPE_BY_SUBSCRIPTION_PLAN: Record<PaidSubscriptionPlan, string> = {
  [UserSubscriptionPlan.SUPER_LEARNER]: "super_learner",
  [UserSubscriptionPlan.ELITE]: "elite",
};

const BillingPeriodToggle = React.memo(function BillingPeriodToggle({
  billingPeriod,
  onBillingPeriodChange,
  annualDiscountPercentage,
  lang,
}: {
  billingPeriod: BillingPeriod;
  onBillingPeriodChange: (billingPeriod: BillingPeriod) => void;
  // Null until the live prices have loaded: the badge is only shown once it can be computed from them.
  annualDiscountPercentage: number | null;
  lang: MarketingPageLanguage;
}) {
  const theme = useLandingTheme();
  const pricingCopy = getMarketingPricingCopy(lang);
  const selectMonthly = useCallback(() => onBillingPeriodChange("monthly"), [onBillingPeriodChange]);
  const selectAnnual = useCallback(() => onBillingPeriodChange("annual"), [onBillingPeriodChange]);

  const annualDiscountNote = annualDiscountPercentage ? pricingCopy.annualDiscountNote(annualDiscountPercentage) : undefined;
  const discountBadgeStyle = {
    bg: theme.color.accent,
    color: theme.button.primaryColor,
    fontSize: "12px",
    fontWeight: 700,
    px: "8px",
    py: "2px",
    borderRadius: "999px",
  } as const;

  // On a phone the two labels and the discount note do not fit on one line in any language (German needed
  // 534px of a 390px screen), which made the whole page scroll sideways. So there the labels may wrap inside
  // two equal halves and the note sits under the toggle; from tablet width up it is inside the yearly button.
  const renderToggleButton = (isSelected: boolean, label: string, onClick: () => void, badge?: string) => (
    <Button
      onClick={onClick}
      aria-pressed={isSelected}
      variant="unstyled"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap="8px"
      flex={{ base: "1 1 0", md: "0 1 auto" }}
      minW={0}
      h="auto"
      minH="44px"
      px={{ base: "12px", md: "20px" }}
      py="6px"
      borderRadius="999px"
      bg={isSelected ? theme.color.surface : "transparent"}
      boxShadow={isSelected ? theme.card.shadow : "none"}
      color={isSelected ? theme.color.heading : theme.color.muted}
      fontFamily={theme.font.body}
      fontSize={{ base: "15px", md: "16px" }}
      fontWeight={600}
      lineHeight={1.25}
      whiteSpace="normal"
      textAlign="center"
      transition="background-color 0.2s ease, color 0.2s ease"
      _hover={{ color: theme.color.heading }}
    >
      {label}
      {badge && (
        <Box as="span" display={{ base: "none", md: "inline-block" }} whiteSpace="nowrap" {...discountBadgeStyle}>
          {badge}
        </Box>
      )}
    </Button>
  );

  return (
    <Flex direction="column" align="center" gap="10px">
      {/* Full width (to a limit) on a phone, so short labels like "pay monthly" get their half and stay on one line. */}
      <Flex bg={theme.color.surfaceAlt} borderRadius="999px" p="4px" gap="2px" w={{ base: "100%", md: "auto" }} maxW={{ base: "360px", md: "100%" }}>
        {renderToggleButton(billingPeriod === "monthly", pricingCopy.payMonthly, selectMonthly)}
        {renderToggleButton(billingPeriod === "annual", pricingCopy.payYearly, selectAnnual, annualDiscountNote)}
      </Flex>
      {/* The phone copy of the note. Its line is kept before the prices load, so the plans below do not jump. */}
      <Box display={{ base: "block", md: "none" }} minH="22px" textAlign="center">
        {annualDiscountNote && (
          <Box as="span" data-annual-discount-note-under-toggle display="inline-block" {...discountBadgeStyle}>
            {annualDiscountNote}
          </Box>
        )}
      </Box>
    </Flex>
  );
});

const PricingPlanCard = React.memo(function PricingPlanCard({
  planName,
  priceLabel,
  pricePeriodLabel,
  billingNoteLabel,
  featuresLeadIn,
  features,
  callToActionLabel,
  callToActionHref,
  onCallToActionClick,
  isHighlighted,
}: {
  planName: string;
  priceLabel: string;
  pricePeriodLabel: string;
  billingNoteLabel: string | null;
  // e.g. "everything in Pro, and:" above the Elite list.
  featuresLeadIn: string | null;
  features: string[];
  callToActionLabel: string;
  callToActionHref: string;
  onCallToActionClick: () => void;
  isHighlighted: boolean;
}) {
  const theme = useLandingTheme();
  return (
    <ThemedPanel
      p={{ base: "28px", md: "36px" }}
      display="flex"
      flexDirection="column"
      border={isHighlighted ? `2px solid ${theme.color.accent}` : theme.card.border}
    >
      <Text fontFamily={theme.font.display} fontWeight={700} fontSize="24px" color={theme.color.heading}>
        {planName}
      </Text>
      <Flex align="baseline" gap="6px" mt="16px">
        <Text
          fontFamily={theme.font.display}
          fontWeight={700}
          fontSize={{ base: "44px", md: "52px" }}
          lineHeight={1}
          color={theme.color.heading}
        >
          {priceLabel}
        </Text>
        <Text fontFamily={theme.font.body} fontSize="18px" color={theme.color.muted}>
          {pricePeriodLabel}
        </Text>
      </Flex>
      {/* Keep the card height stable whether or not there is a billing note. */}
      <Text fontFamily={theme.font.body} fontSize="15px" color={theme.color.muted} mt="8px" minH="22px">
        {billingNoteLabel ?? ""}
      </Text>
      {featuresLeadIn && (
        <Text fontFamily={theme.font.body} fontSize="16px" fontWeight={700} color={theme.color.heading} mt="24px">
          {featuresLeadIn}
        </Text>
      )}
      <List spacing="12px" mt={featuresLeadIn ? "12px" : "28px"} mb="32px" flex="1">
        {features.map((feature) => (
          <ListItem
            key={feature}
            display="flex"
            alignItems="flex-start"
            gap="12px"
            fontFamily={theme.font.body}
            fontSize="17px"
            lineHeight={1.5}
            color={theme.color.text}
          >
            <Box as="span" flexShrink={0} mt="6px" display="inline-flex" aria-hidden="true">
              <IconCheckmark width={16} height={13} color={theme.color.accent} />
            </Box>
            {feature}
          </ListItem>
        ))}
      </List>
      <Button
        as="a"
        href={callToActionHref}
        onClick={onCallToActionClick}
        w="100%"
        h="56px"
        bg={isHighlighted ? theme.color.accent : theme.color.accentSoft}
        color={isHighlighted ? theme.button.primaryColor : theme.color.accentDark}
        border={theme.button.border}
        boxShadow={theme.button.shadow}
        fontFamily={theme.font.body}
        fontSize="18px"
        fontWeight={600}
        borderRadius={theme.shape.buttonRadius}
        transition={landing.buttonHoverTransition}
        _hover={{
          bg: isHighlighted ? theme.color.accentHover : theme.color.border,
          transform: theme.button.hoverTransform,
          textDecoration: "none",
        }}
      >
        {callToActionLabel}
      </Button>
    </ThemedPanel>
  );
});

/**
 * The `pricing` template: the heading from the front matter, a monthly / yearly toggle and the Free, Pro
 * and Elite plan cards. Prices are fetched live through the same code as the app's paywall (Stripe
 * prices via the worker, stripe-price-details.ts), so this page can never disagree with what the visitor is charged. A visitor is
 * anonymous here, so the prices are the US ones; the app shows the local price once it knows the country.
 */
export const MarketingPricingPage = React.memo(function MarketingPricingPage({ entry }: MarketingTemplatePageProps) {
  const theme = useLandingTheme();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("annual");
  const [subscriptionPricingDetails, setSubscriptionPricingDetails] = useState<SubscriptionPricingDetails | null>(null);
  const pricingCopy = getMarketingPricingCopy(entry.lang);
  const isAnnual = billingPeriod === "annual";
  const isHydrated = useIsHydrated();

  useEffect(() => {
    let isPageStillMounted = true;
    loadSubscriptionPricingDetails().then((loadedPricingDetails) => {
      if (isPageStillMounted) setSubscriptionPricingDetails(loadedPricingDetails);
    });
    return () => {
      isPageStillMounted = false;
    };
  }, []);

  const currencySymbol = String(subscriptionPricingDetails?.priceDetails?.symbol ?? "");
  const formatPriceWithCurrencySymbol = (formattedAmount: string | null | undefined) =>
    formattedAmount ? `${currencySymbol}${formattedAmount}` : null;

  const buildPaidPlanCardProps = (subscriptionPlan: PaidSubscriptionPlan) => {
    const pricePerMonth = formatPriceWithCurrencySymbol(
      isAnnual
        ? getAnnualProratedMonthlyPriceAmountForPlan(subscriptionPricingDetails, subscriptionPlan)
        : getMonthlyPriceAmountForPlan(subscriptionPricingDetails, subscriptionPlan)
    );
    const fullAnnualPrice = formatPriceWithCurrencySymbol(
      getFullAnnualPriceForPlan(subscriptionPricingDetails, subscriptionPlan)
    );
    return {
      priceLabel: pricePerMonth ?? pricingCopy.priceLoading,
      pricePeriodLabel: pricePerMonth ? pricingCopy.perMonth : "",
      billingNoteLabel:
        isAnnual && fullAnnualPrice
          ? `${pricingCopy.billedYearly} · ${pricingCopy.fullAnnualPriceNote(fullAnnualPrice)}`
          : null,
      // On jungleai.com this is https://app.jungleai.com/upgrade?…, carrying the visitor's attribution params.
      callToActionHref: buildAppUrl(
        "/upgrade",
        {
          subscription_period_type: billingPeriod,
          subscription_plan_type: UPGRADE_PAGE_PLAN_TYPE_BY_SUBSCRIPTION_PLAN[subscriptionPlan],
        },
        { withVisitorParams: isHydrated, localeHint: MARKETING_LOCALES[entry.lang].productLocale }
      ),
    };
  };

  // The worker may not return Elite prices for every country; without a price the Elite card is left out.
  const hasElitePrice = !subscriptionPricingDetails || Boolean(subscriptionPricingDetails.formattedEliteMonthlyPrice);
  // One badge on the toggle covers every paid card shown, and each plan has its own yearly saving: the shared
  // helper computes them per plan and shows the smaller, so the badge never overstates either card.
  const paidPlansShown: PaidSubscriptionPlan[] = hasElitePrice
    ? [UserSubscriptionPlan.SUPER_LEARNER, UserSubscriptionPlan.ELITE]
    : [UserSubscriptionPlan.SUPER_LEARNER];
  const annualDiscountPercentage = subscriptionPricingDetails
    ? calculateAnnualDiscountPercentageFromRawPrices(subscriptionPricingDetails.priceDetails, paidPlansShown)
    : null;

  const proPlanCardProps = buildPaidPlanCardProps(UserSubscriptionPlan.SUPER_LEARNER);
  const elitePlanCardProps = buildPaidPlanCardProps(UserSubscriptionPlan.ELITE);
  const freePlanCallToActionHref = useAppUrl("/signup");

  const trackFreePlanClick = useCallback(() => trackLandingCallToActionClick("pricing_free"), []);
  const trackProPlanClick = useCallback(
    () => trackLandingCallToActionClick("pricing_pro", { billingPeriod }),
    [billingPeriod]
  );
  const trackElitePlanClick = useCallback(
    () => trackLandingCallToActionClick("pricing_elite", { billingPeriod }),
    [billingPeriod]
  );

  return (
    <MarketingPageMain template="pricing">
      <article className="marketing-article">
        {/* The opened URL owns the language; the footer offers published counterparts. */}
        <h1 className="marketing-article-heading">{getMarketingPageHeading(entry)}</h1>
      </article>
      <ThemedContainer pb={{ base: "64px", lg: "96px" }}>
        <BillingPeriodToggle
          billingPeriod={billingPeriod}
          onBillingPeriodChange={setBillingPeriod}
          annualDiscountPercentage={annualDiscountPercentage}
          lang={entry.lang}
        />
        <SimpleGrid
          columns={{ base: 1, md: hasElitePrice ? 1 : 2, lg: hasElitePrice ? 3 : 2 }}
          spacing={{ base: "20px", lg: "24px" }}
          mt={{ base: "32px", md: "40px" }}
          maxW={{ base: "520px", lg: "none" }}
          mx="auto"
          alignItems="stretch"
        >
          <PricingPlanCard
            planName={pricingCopy.freePlanName}
            priceLabel={`${currencySymbol || "$"}0`}
            pricePeriodLabel={pricingCopy.perMonth}
            billingNoteLabel={null}
            featuresLeadIn={null}
            features={pricingCopy.freePlanFeatures}
            callToActionLabel={pricingCopy.freePlanCallToAction}
            callToActionHref={freePlanCallToActionHref}
            onCallToActionClick={trackFreePlanClick}
            isHighlighted={false}
          />
          <PricingPlanCard
            planName={pricingCopy.proPlanName}
            {...proPlanCardProps}
            featuresLeadIn={pricingCopy.proIncludes}
            features={[
              pricingCopy.proUnlimitedGenerations,
              pricingCopy.proThreeTimesMoreQuestions,
              pricingCopy.proUnlimitedAdvancedQuestionTypes,
              pricingCopy.proUnlimitedAiExplanations,
              pricingCopy.proUnlimitedLectureRecordings,
              pricingCopy.proExportCardsToAnki,
            ]}
            callToActionLabel={pricingCopy.proPlanCallToAction}
            onCallToActionClick={trackProPlanClick}
            isHighlighted={!hasElitePrice}
          />
          {hasElitePrice && (
            <PricingPlanCard
              planName={pricingCopy.elitePlanName}
              {...elitePlanCardProps}
              featuresLeadIn={pricingCopy.eliteEverythingInPro}
              features={[
                pricingCopy.eliteQualityBoost,
                pricingCopy.eliteExamTemplates,
                pricingCopy.eliteAutoImageOcclusion,
                pricingCopy.eliteAiVoiceTutorCalls,
                pricingCopy.eliteDailyReviewSize,
              ]}
              callToActionLabel={pricingCopy.elitePlanCallToAction}
              onCallToActionClick={trackElitePlanClick}
              isHighlighted
            />
          )}
        </SimpleGrid>
        <Text fontFamily={theme.font.body} fontSize="15px" color={theme.color.muted} textAlign="center" mt="28px">
          {pricingCopy.localPricingNote}
        </Text>
      </ThemedContainer>
    </MarketingPageMain>
  );
});
